import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { 
  Container,
  Typography,
  Button,
  CircularProgress,
  Box,
  Paper,
  Grid,
  Divider
} from '@mui/material';
import { 
  CheckCircleOutline,
  WarningAmber 
} from '@mui/icons-material';
import * as XLSX from 'xlsx';

export default function AddDataToProject() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const location = useLocation();
  const { projectID, main_project_columns, max_codini_id } = location.state || {};


  // State variables
  const [file, setFile] = useState(null);
  const [fileValid, setFileValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [jsonData, setJsonData] = useState(null);

  const [columnStats, setColumnStats] = useState({});
  const [totalRows, setTotalRows] = useState(0);

  // Handle file upload and process Excel data
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      const reader = new FileReader();
      reader.onload = (event) => {
        const data = event.target.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        const firstSheetName = workbook.SheetNames[0];
        const firstSheet = workbook.Sheets[firstSheetName];
        const firstSheetData = XLSX.utils.sheet_to_json(firstSheet, { defval: '' });

        if (firstSheetData.length > 0) {
          const headers = Object.keys(firstSheetData[0]).filter((header) => {
            const trimmedHeader = header.trim();
            return trimmedHeader !== '' && !trimmedHeader.startsWith('__EMPTY');
          });
          const projectColumnNames = main_project_columns.map((col) => col.name);

          // Check if column names match
          if (JSON.stringify(headers) === JSON.stringify(projectColumnNames)) {
                        // Calculate column statistics
            const stats = {};
            headers.forEach(header => {
              const count = firstSheetData.filter(row => row[header] !== '').length;
              stats[header] = count;
            });
            setColumnStats(stats);
            setTotalRows(firstSheetData.length);

            setFileValid(true);

            // Transform data and add codini_id
            const transformedData = firstSheetData.map((row, index) => {
              const filteredRow = {};
              headers.forEach(header => {
                const column = main_project_columns.find(col => col.name === header);
                filteredRow[column.columnNumber] = row[header];
              });
              filteredRow.codini_id = max_codini_id + index + 1;
              return filteredRow;
            });
            setJsonData(transformedData);
          } else {
            console.error('Column names do not match!');
            setFileValid(false);
            
          }
        } else {
          setFileValid(false);
          setError(t('fileEmpty') || 'The file is empty.');
        }
      };
      reader.readAsBinaryString(selectedFile);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      setError(t('selectFile') || 'Please select a file.');
      return;
    }
    if (!fileValid || !jsonData) {
      setError(
        t('columnsDoNotMatch') ||
          'The columns in the new Excel file do not match the project columns.'
      );
      return;
    }
  
    setLoading(true);
  
    try {
      // Add data to the project
      const formData = new FormData();
      const dataProcess = 'updateData'
      formData.append('projectID', projectID);
      formData.append('dataProcess', dataProcess);
      formData.append("userEmail", currentUser.email);
      formData.append('jsonData', JSON.stringify(jsonData));
      const response = await fetch('https://codini-project-adddata-gt-795950092325.europe-west3.run.app', {
        method: 'POST',
        body: formData,
      });
  
      if (!response.ok) {
        throw new Error(t('addDataFailed') || 'Failed to add data.');
      }
  
      // Identify Verbatim columns
      const verbatimColumns = main_project_columns.filter(col => col.type === "Verbatim");

      const idColumn = main_project_columns.find(col => col.type === "ID");
      const idColumnName = idColumn.columnNumber;

     
      
  
      // Function to send Verbatim data
      const sendVerbatimData = async (column) => {
        // Extract data for this column from jsonData

        const columnData = jsonData.map(row => ({
          codini_id: row.codini_id,
          record_id: row[idColumnName] || '' ,
          text: row[column.columnNumber] || '' // Use empty string if no data         
        }));

        const formData1 = new FormData();
        const dataProcess1 = 'processData'
        formData1.append('projectID', projectID);
        formData1.append('dataProcess', dataProcess1);
        formData1.append('columnNo', column.columnNumber);
        formData1.append("userEmail", currentUser.email);
        formData1.append('columnData', JSON.stringify(columnData));
  
        try {
          const verbatimResponse = await fetch('https://codini-project-adddata-gt-795950092325.europe-west3.run.app', {
            method: 'POST',
            body: formData1,
          });
  
          if (!verbatimResponse.ok) {
            throw new Error(`Failed to process Verbatim data for column ${column.name}`);
          }
        } catch (err) {
          console.error(`Failed to send Verbatim data for column ${column.name}:`, err);
        }
      };
  
      // Send requests for all Verbatim columns concurrently without awaiting
      verbatimColumns.forEach(column => sendVerbatimData(column));

      await new Promise(resolve => setTimeout(resolve, 5000));
  
      // Navigate back immediately after initiating requests
      navigate('/ProjectsPage');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const renderStatistics = () => (
    <Paper elevation={3} sx={{ mt: 3, p: 3, borderRadius: 2, backgroundColor: '#f8f9fa' }}>
      <Typography variant="h6" sx={{ mb: 2, color: '#2c3e50', fontWeight: 'bold' }}>
        {t('dataStatistics') || 'Data Overview'}
      </Typography>
      <Divider sx={{ mb: 2 }} />
      
      <Grid container spacing={2}>
        {Object.entries(columnStats).map(([column, count]) => (
          <Grid item xs={12} sm={6} md={4} key={column}>
            <Paper elevation={0} sx={{ p: 2, backgroundColor: '#e9ecef' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: '600', color: '#495057' }}>
                {column}
              </Typography>
              <Typography variant="body1" sx={{ color: count > 0 ? '#2a9d8f' : '#e76f51' }}>
                {t('filledCells') || 'Filled Cells'}: {count}
              </Typography>
              <Typography variant="body2" sx={{ color: '#6c757d' }}>
                ({((count / totalRows) * 100).toFixed(1)}%)
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>
      
      <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #dee2e6' }}>
        <Typography variant="h6" sx={{ color: '#2c3e50' }}>
          {t('totalRecords') || 'Total Records'}:{' '}
          <span style={{ color: '#2a9d8f' }}>{totalRows}</span>
        </Typography>
      </Box>
    </Paper>
  );

  return (
    <Box sx={{ backgroundColor: '#f4f4f4', minHeight: '100vh', padding: '20px' }}>
      <Container sx={{ mt: 1, backgroundColor: '#fff', borderRadius: 2, padding: '3px' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, mb: 2 }}>
          <Typography variant="h5" gutterBottom>
            {t('addDataToProject') || 'Add Data to Project'}
          </Typography>
          <Button variant="outlined" onClick={() => navigate('/ProjectsPage')}>
            {t('cancel') || 'Cancel'}
          </Button>
        </Box>

        <Paper 
          elevation={0}
          sx={{
            mb: 2,
            p: 2,
            backgroundColor: '#f8f9fa',
            borderLeft: '4px solid #fb5607',
            borderRadius: 1
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#2c3e50', mb: 1 }}>
            {t('uploadGuidelines') || 'Upload Guidelines'}
          </Typography>
          <Box component="ul" sx={{ pl: 2.5, color: '#6c757d', '& li': { mb: 1 } }}>
            <Typography component="li" variant="body2">
              {t('guideline1') || 'Excel file must have the same column structure as the original project'}
            </Typography>
            <Typography component="li" variant="body2">
              {t('guideline2') || 'Do not re-upload existing records - only new entries'}
            </Typography>
            <Typography component="li" variant="body2">
              {t('guideline3') || 'Existing codebooks will be applied to new data automatically'}
            </Typography>
            <Typography component="li" variant="body2">
              {t('guideline4') || 'Analysis will start immediately after successful upload'}
            </Typography>
          </Box>
        </Paper>
        <form onSubmit={handleSubmit}>
          <Box sx={{ mt: 2 }}>
            <input
              type="file"
              accept=".xlsx"
              onChange={handleFileChange}
              style={{ display: 'none' }}
              id="file-input"
            />
            <label htmlFor="file-input">
              <Button variant="contained" component="span" sx={{ background: '#fb5607' }}>
                {t('selectFile') || 'Select File'}
              </Button>
            </label>
            {file && <Typography variant="body2" sx={{ mt: 1 }}>{file.name}</Typography>}
          </Box>

          {file && (
            <Paper 
              elevation={0} 
              sx={{ 
                mt: 2, 
                p: 2,
                backgroundColor: fileValid ? '#e8f5e9' : '#ffebee',
                borderLeft: `4px solid ${fileValid ? '#2a9d8f' : '#e76f51'}`,
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5
              }}
            >
              {fileValid ? (
                <CheckCircleOutline sx={{ color: '#2a9d8f', fontSize: 28 }} />
              ) : (
                <WarningAmber sx={{ color: '#e76f51', fontSize: 28 }} />
              )}
              <Box>
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    fontWeight: 600, 
                    color: fileValid ? '#2a9d8f' : '#e76f51' 
                  }}
                >
                  {fileValid ? t('validationSuccess') : t('validationError')}
                </Typography>
                <Typography variant="body2" sx={{ color: '#6c757d' }}>
                  {fileValid
                    ? (t('fileValid') || 'File structure validated successfully')
                    : (t('columnsDoNotMatch') || 'Column mismatch detected in uploaded file')}
                </Typography>
              </Box>
            </Paper>
          )}

          {fileValid && renderStatistics()}

          {error && <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>}

          <Box sx={{ mt: 2, mb: 2 }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading || !fileValid}
            >
              {loading ? <CircularProgress size={24} /> : (t('addData') || 'Add Data')}
            </Button>
          </Box>
        </form>
      </Container>
    </Box>
  );
}